import moment from "moment";
import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import Aggrid from "../../../framework/AgGrid";
import CellNumberEditor from "../../../framework/cellEditor/CellNumberEditor";
import GlobalContext from "../../../framework/GlobalContext";
import { fetchDiscountProgramDetails } from "../../../services/discount-program";
import { rowDown, rowUp, selectRow } from "../../../utils/gridUtils";
import classes from "./index.module.css";

const DiscountSlabDetails = (props) => {
  const { discountProgramCode = null, onEscape = () => {} } = props;
  const [discountData, setDiscountData] = useState(null);
  const [rowData, setRowData] = useState(null);
  const [api, setApi] = useState(null);
  const [selectedRowIndex, setSelectedRowIndex] = useState(0);
  const { currentComponent, setCurrentComponent } = useContext(GlobalContext);

  const columnDefs = useMemo(() => {
    return [
      {
        headerName: "Discount Type",
        field: "type",
        width: 200,
        cellRenderer: (params) => {
          if (params.data?.type === "manufacturer") {
            return "Manufacturer";
          } else if (params.data?.type === "category") {
            return "Product Category";
          } else if (params.data?.type === "manufacturer_group") {
            return "Manufacturer Group";
          }
        },
      },
      {
        headerName: "Option",
        field: "typeName",
        width: 200,
        cellRenderer: (params) => {
          if (params.data?.type === "manufacturer") {
            return params.data?.typeData?.manufacturerName;
          } else if (params.data?.type === "category") {
            return params.data?.typeData?.categoryName;
          } else if (params.data?.type === "manufacturer_group") {
            return params.data?.typeName;
          }
        },
      },
      {
        headerName: "Minimum Amount",
        field: "min",
        width: 200,
      },
      {
        headerName: "Maximum Amount",
        field: "max",
        width: 200,
      },
      {
        headerName: "Discount %",
        field: "discPerMedicine",
        width: 200,
        valueGetter: (params) => {
          return (
            params.data?.discPerMedicine || params?.data?.discPerDefault || 0
          );
        },
      },
    ];
  }, []);

  const getDiscountSlabDetails = useCallback((discountProgramCode) => {
    fetchDiscountProgramDetails(`_id=${discountProgramCode}`).then((resp) => {
      const data = resp?.result?.rows;
      setDiscountData(data);
      const updatedRowData = data?.discountProgramDetails?.map((item) => ({
        ...item,
      }));
      setRowData(updatedRowData);
    });
  }, []);

  const onGridReadyHandler = useCallback(
    (params) => {
      setApi(params.api);
      params.api.sizeColumnsToFit();
      selectRow(params.api, selectedRowIndex);
    },
    [selectedRowIndex]
  );

  const keyupListener = useCallback(
    (event) => {
      const key = event.key?.toLowerCase();
      if (key === "escape") {
        onEscape();
        return;
      }
      if (key === "arrowdown") {
        rowDown(api);
        return;
      }
      if (key === "arrowup") {
        rowUp(api);
        return;
      }
    },
    [api, onEscape]
  );

  useEffect(() => {
    if (discountProgramCode) {
      getDiscountSlabDetails(discountProgramCode);
    }
  }, [getDiscountSlabDetails, discountProgramCode]);

  useEffect(() => {
    setCurrentComponent("grid_discount_slab_details");
  }, [setCurrentComponent]);

  useEffect(() => {
    if (currentComponent === "grid_discount_slab_details") {
      document.addEventListener("keyup", keyupListener, false);
    }
    return () => {
      document.removeEventListener("keyup", keyupListener, false);
    };
  }, [api, currentComponent, keyupListener]);

  return (
    <div className={classes["discount-slab-details"]}>
      <div className={classes["discount-slab-details-header"]}>
        <div className={classes["discount-slab-details-header-left"]}>
          <span>
            {discountData?.programName?.toUpperCase()} -{" "}
            {discountData?.discountProgramCode}
          </span>
          <span>
            From - {moment(`${discountData?.startDate}`).format("DD/MM/YYYY")}
          </span>
          <span>
            To - {moment(`${discountData?.endDate}`).format("DD/MM/YYYY")}
          </span>
        </div>
        <div className={classes["discount-slab-details-header-right"]}>
          {discountData?.discountProgramDetails[0]?.entity && (
            <span>
              Entity -{" "}
              {discountData?.discountProgramDetails[0]?.entity?.entityName}
            </span>
          )}
          {discountData?.discountProgramDetails[0]?.categoryName && (
            <span>
              Product Category -{" "}
              {discountData?.discountProgramDetails[0]?.categoryName?.toUpperCase()}{" "}
            </span>
          )}
          {discountData?.discountProgramDetails[0]?.patientCategoryName && (
            <span>
              Customer -{" "}
              {discountData?.discountProgramDetails[0]?.patientCategoryName?.toUpperCase()}{" "}
            </span>
          )}
          {discountData?.discountProgramDetails[0]?.entityName && (
            <span>
              Entity -{" "}
              {discountData?.discountProgramDetails[0]?.entityName?.toUpperCase()}{" "}
            </span>
          )}
        </div>
      </div>

      <div className={classes["discount-slab-details-grid"]}>
        <Aggrid
          id="grid_discount_slab_details"
          className="ag-theme-balham"
          rowData={rowData}
          columnDefs={columnDefs}
          frameworkComponents={{
            cellNumberEditor: CellNumberEditor,
          }}
          defaultColDef={{
            resizable: true,
            cellStyle: (params) => {
              return {
                borderRight: "1px solid #a0a0a0",
              };
            },
            suppressKeyboardEvent: (params) => true,
          }}
          onGridReady={(params) => {
            setTimeout(() => {
              onGridReadyHandler(params);
            }, 100);
          }}
          onCellClicked={(params) => {
            setSelectedRowIndex(params.rowIndex);
          }}
        />
      </div>
    </div>
  );
};

export default DiscountSlabDetails;
